import React from "react";
import CountdownComponent from "../countdown/countdown";
import { Countdown } from "../../../types";
import { cn } from "../../../utils/cn";
import BackButton from "../back-button/back-button";
import Button from "@/components/ui/button";

type Props = {
  onBack?: () => void;
  countdown?: Countdown;
  onSubmit?: (event: React.FormEvent<HTMLFormElement>) => void;
  error?: string;

  loading?: boolean;
  ready?: boolean;
  loadingText?: string;
  submitText?: string;

  submitTexts?: string[];

  showButton?: boolean;
  countdownThreshold?: number;
  readyFlash?: boolean;
};

const FormFooter: React.FunctionComponent<Props> = (props) => {
  // 1. Find the longest possible text for the button to prevent resizing.
  // We gather all potential text values...
  const allPossibleTexts = [
    props.loadingText ?? "loading...",
    props.submitText ?? "submit",
    ...(props.submitTexts ?? []), // ...including the array of possible texts
  ];

  // ...and find the one with the most characters.
  const longestText = allPossibleTexts.reduce(
    (a, b) => (a.length > b.length ? a : b),
    ""
  );

  // Determine the text that should be visible right now.
  const currentButtonText = props.loading
    ? (props.loadingText ?? "loading...")
    : props.submitText
      ? props.submitText
      : "submit";

  return (
    <div
      className={
        "flex flex-row justify-between items-stretch h-14 p-0.5 text-2xl bg-gray-med sticky border-solid border-2 border-gray-drker border-r-gray-light border-b-gray-light border-t-gray-shadow border-t-4"
      }
    >
      <div className={"self-stretch flex justify-center items-center"}>
        {props.onBack ? (
          <BackButton onBack={props.onBack} className={"self-stretch"} />
        ) : props.countdown ? (
          <CountdownComponent
            countdown={props.countdown}
            threshold={props.countdownThreshold}
          />
        ) : (
          ""
        )}
      </div>

      <div
        className={
          "self-stretch flex justify-center items-center flex-1 min-h-0"
        }
      >
        {props.error ? (
          <div
            className={
              "p-0.5 relative text-center max-h-full text-sm leading-tight overflow-hidden mx-0.5 border-dashed border-black"
            }
          >
            {props.error}
          </div>
        ) : (
          ""
        )}
      </div>

      <div className={"self-stretch flex justify-center items-center"}>
        {props.onSubmit ? (
          // 2. Use a container with a "sizer" element to set a consistent width.
          <div className="relative self-stretch">
            {/* This "sizer" span is the secret. It's invisible but contains the 
              longest possible text. This makes the parent `div` stretch to the
              perfect width. Its styles (padding, font) must match the button's.
            */}
            <span
              className="invisible h-full inline-flex items-center text-lg uppercase font-sans px-8"
              aria-hidden="true"
            >
              {longestText}
            </span>

            {/*
              The actual button is positioned absolutely to fill the space
              created by the sizer. It will have the correct width now.
            */}
            <Button
              type="submit"
              className={cn(
                "absolute inset-0 w-full h-full",
                props.ready && props.readyFlash && "animate-submit-flash"
              )}
              size={"xl"}
              disabled={props.loading || !props.ready}
            >
              {currentButtonText}
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default FormFooter;
